// Pure derivation for the cross-market submission overview.
//
// Groups the open session's challenges by resource and folds in per-challenge
// coverage to answer, per market: "are all my forecasts in?". Consumed by the
// StatusHero right rail and the header resource selector status dots.

import {resourceDisplayName} from './resourceName';

// Shared presentation for market-state dots (StatusHero rail + header selector).
export const MARKET_STATE_DOT_CLASS = {
  ready: 'bg-green-500',
  pending: 'bg-amber-500',
  missed: 'bg-gray-300 dark:bg-gray-600',
};

export const MARKET_STATE_TITLE = {
  ready: 'All forecasts submitted',
  pending: 'Forecasts pending for this market',
  missed: 'Not all forecasts were submitted before gate closure',
};

// Markets needing action first, preserving resource order within each group.
export const orderMarketRows = (rows) => [
  ...rows.filter((r) => r.state !== 'ready'),
  ...rows.filter((r) => r.state === 'ready'),
];

/**
 * @returns null for observers or when the session has no challenges,
 * otherwise {rows, marketCount, readyCount, dueCount, allReady, closed}
 * where each row is {id, name, total, ready, state: 'ready'|'pending'|'missed'}.
 */
export const deriveMarketsOverview = ({
  resources = [],
  challenges = [],
  coverage = {},
  isForecaster = true,
  closed = false,
} = {}) => {
  if (!isForecaster) return null;

  const byResource = new Map();
  challenges.forEach((c) => {
    if (!c.resourceId) return;
    const entry = byResource.get(c.resourceId) || {total: 0, ready: 0};
    entry.total += 1;
    if (coverage?.[c.id]?.ready) entry.ready += 1;
    byResource.set(c.resourceId, entry);
  });
  if (byResource.size === 0) return null;

  // Keep the user's resource ordering so rows don't jump as coverage updates;
  // resources missing from the user list (shouldn't happen) go last.
  const ordered = [
    ...resources.filter((r) => byResource.has(r.id)).map((r) => r.id),
    ...[...byResource.keys()].filter((id) => !resources.some((r) => r.id === id)),
  ];

  const nameOf = (id) =>
    resourceDisplayName(resources.find((r) => r.id === id))
    || challenges.find((c) => c.resourceId === id)?.resourceName
    || String(id);

  const rows = ordered.map((id) => {
    const {total, ready} = byResource.get(id);
    let state;
    if (ready === total) {
      state = 'ready';
    } else {
      state = closed ? 'missed' : 'pending';
    }
    return {id, name: nameOf(id), total, ready, state};
  });

  const readyCount = rows.filter((r) => r.state === 'ready').length;
  const dueForecastCount = rows.reduce((acc, r) => acc + (r.total - r.ready), 0);
  return {
    rows,
    marketCount: rows.length,
    readyCount,
    dueCount: rows.length - readyCount,
    dueForecastCount,
    allReady: readyCount === rows.length,
    closed,
  };
};
